import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { can } from '../auth/me';
import { useMe } from '../auth/useMe';
import { REPEAT_LABELS, type Repeat } from '../company-tasks/types';
import { todayInWarsaw } from '../events/calendar';
import { formatDayText } from '../events/fields';
import { ConflictNotice } from '../ui/ConflictNotice';
import { DialogLayer, useReturnFocus } from '../ui/Dialog';
import { leaveGuarded, useUnsavedGuard } from '../ui/unsaved';
import { trapTab } from '../ui/focus';
import { QuickPicker, type PickerOption } from '../ui/QuickPicker';
import { RowMenu } from '../ui/RowMenu';
import { PrivateReminders } from './PrivateReminders';
import { PRIVATE_BASE, PRIVATE_KEY, type PrivateTask } from './types';

type Ref = { id: string; name: string };
type Option = PickerOption & { id: string };

async function searchEvents(q: string, signal: AbortSignal): Promise<Option[]> {
  const res = await api<{ events: { id: string; name?: string; startsAt?: string }[] }>(`/api/events?q=${encodeURIComponent(q)}`, { signal });
  return res.events.slice(0, 20).map((e) => ({ key: e.id, id: e.id, name: e.name ?? 'Wydarzenie', details: e.startsAt ? [formatDayText(todayInWarsaw(new Date(e.startsAt)))] : [] }));
}
async function searchContractors(q: string, signal: AbortSignal): Promise<Option[]> {
  const res = await api<{ contractors: Ref[] }>(`/api/contractors?q=${encodeURIComponent(q)}`, { signal });
  return res.contractors.slice(0, 20).map((c) => ({ key: c.id, id: c.id, name: c.name, details: [] }));
}

type Draft = { title: string; notes: string; dueDay: string; dueTime: string; repeat: '' | Repeat; done: boolean; event: Ref | null; contractor: Ref | null };
const draftOf = (t: PrivateTask): Draft => ({
  title: t.title,
  notes: t.notes,
  dueDay: t.dueDay ?? '',
  dueTime: t.dueTime ?? '',
  repeat: t.repeat ?? '',
  done: t.done,
  // a link the author no longer sees keeps its id; shown as "(brak dostępu)"
  event: t.eventId ? { id: t.eventId, name: t.eventName ?? '(brak dostępu)' } : null,
  contractor: t.contractorId ? { id: t.contractorId, name: t.contractorName ?? '(brak dostępu)' } : null,
});

/** The fields of `d` that differ from `t`, as the server takes them. */
export function changedBody(t: PrivateTask, d: Draft): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (d.title.trim() !== t.title) out.title = d.title.trim();
  if (d.notes.trim() !== t.notes) out.notes = d.notes.trim();
  if ((d.dueDay || null) !== t.dueDay) out.dueDay = d.dueDay || null;
  // no day: no time and no repeat
  const time = d.dueDay ? d.dueTime || null : null;
  const repeat = d.dueDay ? d.repeat || null : null;
  if (time !== t.dueTime) out.dueTime = time;
  if (repeat !== t.repeat) out.repeat = repeat;
  if (d.done !== t.done) out.done = d.done;
  if ((d.event?.id ?? null) !== t.eventId) out.eventId = d.event?.id ?? null;
  if ((d.contractor?.id ?? null) !== t.contractorId) out.contractorId = d.contractor?.id ?? null;
  return out;
}

/** The dialog's box: the opener gets the focus back, Tab stays inside, a click beside closes (asking when changed). */
function DialogFrame({ onClose, focusBox, children }: { onClose: () => void; focusBox: boolean; children: React.ReactNode }) {
  // the opener, recorded once before the dialog takes the focus (its layers come and go while it loads: review M5)
  useReturnFocus();
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (focusBox) box.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (box.current) trapTab(box.current, e);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="dialog-backdrop" onClick={(e) => e.target === e.currentTarget && leaveGuarded(onClose, box.current)}>
      <div className="dialog panel ct-dialog" role="dialog" aria-modal="true" aria-labelledby="pt-dialog-title" tabIndex={-1} ref={box}>
        {children}
      </div>
    </div>
  );
}

/** One of my tasks in a dialog (Escape closes, no "×"; "Usuń" under "…"). */
export function PrivateTaskDialog({ taskId, onClose }: { taskId: string; onClose: () => void }) {
  const task = useQuery({ queryKey: [...PRIVATE_KEY, 'task', taskId], retry: false, queryFn: () => api<PrivateTask>(`${PRIVATE_BASE}/${taskId}`) });
  return (
    <DialogFrame onClose={onClose} focusBox>
      {!task.data && <DialogLayer onClose={onClose} returnFocus={false} />}
      {task.isLoading && <p className="muted" role="status">Wczytywanie…</p>}
      {task.error && (
        <>
          <h2 id="pt-dialog-title">Moje zadanie</h2>
          <p className="alert" role="alert">
            {task.error instanceof ApiError && task.error.status === 404 ? 'Tego zadania już nie ma.' : 'Nie udało się wczytać zadania'}
          </p>
          <div className="actions">
            <button className="btn" type="button" onClick={onClose}>
              Zamknij
            </button>
          </div>
        </>
      )}
      {task.data && <TaskForm key={task.data.version} task={task.data} onClose={onClose} />}
    </DialogFrame>
  );
}

/** What the one line of "Moje zadania" read: the new task's dialog starts with it. */
export type NewTaskStart = { title: string; dueDay: string | null; dueTime: string | null };
const BLANK: PrivateTask = { id: '', title: '', notes: '', dueDay: null, dueTime: null, repeat: null, eventId: null, eventName: null, contractorId: null, contractorName: null, done: false, doneAt: null, version: 0 };

/**
 * A new task from the one line (owner 2026-11-01): the same dialog as for a task, with what was typed; Enter (⌘/Ctrl+Enter
 * in the notes) adds it — what one Enter in the line added before, plus what was filled in here. Esc / "Anuluj" add
 * nothing (the line keeps its text). Reminders need the task: they are planned in its dialog once it is added.
 */
export function NewPrivateTaskDialog({ start, onClose, onCreated }: { start: NewTaskStart; onClose: () => void; onCreated: (t: PrivateTask) => void }) {
  return (
    <DialogFrame onClose={onClose} focusBox={false}>
      <TaskForm task={{ ...BLANK, ...start }} onClose={onClose} onCreated={onCreated} />
    </DialogFrame>
  );
}

function TaskForm({ task, onClose, onCreated }: { task: PrivateTask; onClose: () => void; onCreated?: (t: PrivateTask) => void }) {
  const { me } = useMe();
  const queryClient = useQueryClient();
  const creating = !!onCreated;
  const [draft, setDraft] = useState<Draft>(() => draftOf(task));
  const titleEl = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!creating) return;
    const el = titleEl.current;
    el?.focus();
    el?.setSelectionRange(el.value.length, el.value.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((d) => ({ ...d, [k]: v }));
  const refresh = () => queryClient.invalidateQueries({ queryKey: PRIVATE_KEY });
  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      creating ? api<PrivateTask>(PRIVATE_BASE, { method: 'POST', body }) : api<PrivateTask>(`${PRIVATE_BASE}/${task.id}`, { method: 'PATCH', body: { version: task.version, ...body } }),
    onSuccess: (saved) => {
      void refresh();
      if (onCreated) onCreated(saved);
      else onClose();
    },
  });
  const remove = useMutation({
    mutationFn: () => api(`${PRIVATE_BASE}/${task.id}?version=${task.version}`, { method: 'DELETE' }),
    onSuccess: () => {
      void refresh();
      onClose();
    },
  });
  const error = save.error ?? remove.error;
  const conflict = error instanceof ApiError && error.status === 409;
  const issues = error instanceof ApiError ? fieldIssues(error) : [];
  const issue = (path: string) => issues.find((i) => i.path === path)?.message;
  const seeEvents = !!me && can(me, 'events.events', 'view');
  const seeContractors = !!me && can(me, 'contractors.contractors', 'view');
  // owner 2026-10-23 13:34: closing (Escape, a click beside) with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”
  const body = changedBody(task, draft);
  // a new task: changes are what differs from what the line gave; it goes out with everything not left empty
  const sent = creating ? changedBody(BLANK, draft) : body;
  const formEl = useRef<HTMLFormElement | null>(null);
  const guardRef = useUnsavedGuard({
    dirty: Object.keys(body).length > 0,
    save: async () => {
      await save.mutateAsync(sent);
      return true;
    },
  });

  return (
    <form
      ref={(el) => ((formEl.current = el), guardRef(el))}
      onSubmit={(e) => {
        e.preventDefault();
        if (save.isPending) return;
        if (creating) save.mutate(sent);
        else if (Object.keys(body).length === 0) onClose();
        else save.mutate(body);
      }}
    >
      <DialogLayer onClose={() => leaveGuarded(onClose, formEl.current)} returnFocus={false} busy={save.isPending || remove.isPending} />
      <div className="pt-dialog-head">
        <h2 id="pt-dialog-title">{creating ? 'Nowe zadanie' : 'Moje zadanie'}</h2>
        {!creating && <RowMenu label="Więcej działań" actions={[{ label: 'Usuń zadanie', danger: true, onSelect: () => setConfirmDelete(true) }]} />}
      </div>
      {confirmDelete && !remove.isPending && (
        <p className="notice" role="status">
          Zadanie zniknie na stałe.{' '}
          <button type="button" className="btn small ct-delete" onClick={() => remove.mutate()}>
            Usuń
          </button>{' '}
          <button type="button" className="btn small ghost" onClick={() => setConfirmDelete(false)}>
            Zostaw
          </button>
        </p>
      )}
      <label className="field">
        <span>Co do zrobienia</span>
        <input ref={titleEl} value={draft.title} onChange={(e) => set('title', e.target.value)} maxLength={200} required autoComplete="off" />
        {issue('title') && <span className="field-error">{issue('title')}</span>}
      </label>
      <div className="ct-row">
        <label className="field">
          <span>Termin (opcjonalnie)</span>
          <input type="date" value={draft.dueDay} onChange={(e) => set('dueDay', e.target.value)} />
          {issue('dueDay') && <span className="field-error">{issue('dueDay')}</span>}
        </label>
        <label className="field">
          <span>Godzina (opcjonalnie)</span>
          <input type="time" value={draft.dueTime} disabled={!draft.dueDay} onChange={(e) => set('dueTime', e.target.value)} />
        </label>
      </div>
      <label className="field">
        <span>Notatki</span>
        <textarea
          value={draft.notes}
          onChange={(e) => set('notes', e.target.value)}
          rows={3}
          maxLength={5000}
          // a new task: ⌘/Ctrl+Enter adds (Enter alone breaks the line), as in the comments of a packing list
          onKeyDown={(e) => {
            if (creating && e.key === 'Enter' && (e.metaKey || e.ctrlKey) && !e.nativeEvent.isComposing) {
              e.preventDefault();
              e.currentTarget.form?.requestSubmit();
            }
          }}
        />
      </label>
      <label className="field">
        <span>Powtarzanie</span>
        <select value={draft.dueDay ? draft.repeat : ''} disabled={!draft.dueDay} onChange={(e) => set('repeat', e.target.value as '' | Repeat)}>
          <option value="">{draft.dueDay ? 'Nie powtarzaj' : 'Nie powtarzaj (wymaga terminu)'}</option>
          {(Object.keys(REPEAT_LABELS) as Repeat[]).map((r) => (
            <option key={r} value={r}>
              {REPEAT_LABELS[r]}
            </option>
          ))}
        </select>
      </label>
      {(seeEvents || seeContractors || draft.event || draft.contractor) && (
        <fieldset className="pt-links">
          <legend>Powiązanie (opcjonalnie, widzisz je tylko Ty)</legend>
          <LinkField label="Wydarzenie" value={draft.event} canPick={seeEvents} search={searchEvents} onChange={(v) => set('event', v)} error={issue('eventId')} />
          <LinkField label="Kontrahent" value={draft.contractor} canPick={seeContractors} search={searchContractors} onChange={(v) => set('contractor', v)} error={issue('contractorId')} />
        </fieldset>
      )}
      <div className="ct-done">
        <label className="ct-check">
          <input type="checkbox" checked={draft.done} onChange={(e) => set('done', e.target.checked)} />
          Zrobione
        </label>
      </div>
      {creating ? <p className="muted">Przypomnienia ustawisz po dodaniu zadania (kliknij je na liście).</p> : !task.done && <PrivateReminders taskId={task.id} hasDueDay={!!task.dueDay} />}
      {conflict && (
        <ConflictNotice
          onReload={() => {
            void refresh();
            save.reset();
            remove.reset();
          }}
        />
      )}
      {error && !conflict && issues.length === 0 && (
        <p className="alert" role="alert">
          {error instanceof ApiError ? error.message : 'Nie udało się zapisać'}
        </p>
      )}
      <div className="actions ct-actions">
        <button className="btn primary" type="submit" disabled={save.isPending}>
          {save.isPending ? 'Zapisywanie…' : creating ? 'Dodaj' : 'Zapisz'}
        </button>
        <button className="btn" type="button" onClick={onClose}>
          Anuluj
        </button>
      </div>
    </form>
  );
}

/** The chosen event or kontrahent with "Odłącz", or a picker (only for whoever may see them). */
function LinkField({ label, value, canPick, search, onChange, error }: { label: string; value: Ref | null; canPick: boolean; search: (q: string, s: AbortSignal) => Promise<Option[]>; onChange: (v: Ref | null) => void; error?: string | undefined }) {
  if (!value && !canPick) return null;
  return (
    <div className="field pt-link-field">
      {value ? (
        <>
          <span>{label}</span>
          <span className="pt-link-chosen">
            {value.name}
            <button type="button" className="btn ghost small" onClick={() => onChange(null)} aria-label={`Odłącz: ${value.name}`}>
              Odłącz
            </button>
          </span>
        </>
      ) : (
        <QuickPicker label={label} placeholder={`Szukaj: ${label.toLowerCase()}`} search={search} onPick={(o) => onChange({ id: o.id, name: o.name })} />
      )}
      {error && <span className="field-error">{error}</span>}
    </div>
  );
}
